import React, { Component } from "react"
import { Button, Form, Overlay } from "react-bootstrap"
import { FaArrowDown, FaArrowUp, FaCaretDown, FaFilter, FaSearch, FaSortAlphaDown, FaSortAlphaDownAlt } from "react-icons/fa"
import { List, ListRowProps } from "react-virtualized"
import clearFilters from "../assets/images/clear-filters.png"
import Checkbox from "./Checkbox"

export type ValueLabels = { [key: string]: string }

class FilterItem {
  constructor(options: { value: string | boolean, checked: boolean, label?: string }) {
    if (typeof options.value === "boolean") {
      this.value = options.value.toString()
      this.label = options.value ? "Yes" : "No"
    } else {
      this.value = options.value || ""
      this.label = options.label || options.value || "(Blank)"
    }
    this.checked = options.checked
  }
  value: string
  checked: boolean
  label: string
}

interface ColumnHeaderFilterProps {
  title: string,
  items: string[],
  filteredItems?: string[],
  message?: React.ReactNode,
  children?: React.ReactNode,
  sort?: "asc" | "desc",
  filtered?: boolean,
  uppercase: boolean,
  offsetLeft?: number,
  offsetTop?: number,
  itemValueLabels?: ValueLabels,
  onFilter?: (items: string[]) => void,
  onSort?: (order?: string) => void
}

interface ColumnHeaderFilterState {
  sort?: "asc" | "desc",
  open: boolean,
  searchText: string,
  items: FilterItem[],
  filtered?: boolean
}

export default class ColumnHeaderFilter extends Component<ColumnHeaderFilterProps, ColumnHeaderFilterState> {
  state: ColumnHeaderFilterState = { open: false, sort: this.props.sort, items: [], searchText: "" }
  nameInput: HTMLInputElement | undefined
  toggleElement = React.createRef<HTMLDivElement>()
  listElement = React.createRef<List>()

  componentDidMount() {
    this.resetFilterItems([])
  }

  componentDidUpdate(prevProps: ColumnHeaderFilterProps) {
    const arraysEqual = (a1: string[], a2: string[]): boolean => {
      if (a1.length !== a2.length) {
        return false
      }
      const a1Sorted = [...a1].filter(item => !!item).map(item => item.toString().toLowerCase()).sort()
      const a2Sorted = [...a2].filter(item => !!item).map(item => item.toString().toLowerCase()).sort()
      return a1Sorted.every((value, index) => value === a2Sorted[index])
    }

    if (this.props.title !== prevProps.title) {
      this.resetFilterItems([])
    } else {
      if (!!this.props.filteredItems) {
        if (!arraysEqual(this.props.filteredItems || [], prevProps.filteredItems || []) || !arraysEqual(this.props.items || [], prevProps.items || [])) {
          this.resetFilterItems(this.props.filteredItems || [])
        }
      } else {
        if (!arraysEqual(this.props.items, prevProps.items)) {
          this.resetFilterItems(this.state.items.filter(item => item.checked).map(item => item.value))
        }
      }
    }
    if (this.props.filtered !== prevProps.filtered) {
      this.setState({ filtered: this.props.filtered })
    }
    if (this.props.sort !== prevProps.sort) {
      this.setState({ sort: this.props.sort })
    }
  }

  resetFilterItems(checkedValues: string[]) {
    const checkAll = checkedValues.length === 0
    const itemValueLabels = this.props.itemValueLabels
    let items = this.props.items ? this.props.items
      .filter((item, index) => this.props.items.indexOf(item) === index)
      .map(item => new FilterItem({
        value: item,
        checked: checkAll || checkedValues.includes(item),
        label: itemValueLabels ? itemValueLabels[item] : undefined
      }))
      : []
    items.unshift(new FilterItem({ value: "#ALL#", checked: checkAll || items.every(item => item.checked), label: "(Select All)" }))
    this.setState({ items: items })
  }

  clearFilter() {
    this.setState({ open: false, filtered: false })
    this.resetFilterItems([])
    this.props.onFilter && this.props.onFilter([])
  }

  toggleDialog = () => this.setState({ open: !this.state.open })
  closeDialog = () => this.setState({ open: false })
  changeTextFilter = (value: string) => this.setState({ searchText: value })
  checkboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const checked = e.target.checked
    const index = this.state.items.findIndex(item => String(item.value) === value)
    if (index === 0) {
      this.setState(prevState => {
        let newItems = [...prevState.items]
        newItems.map(item => item.checked = checked)
        return { items: newItems }
      })
    } else {
      this.setState(prevState => {
        const newItems = [...prevState.items]
        newItems[index].checked = checked
        newItems[0].checked = newItems.slice(1).every((item) => item.checked)
        return { items: newItems }
      })
    }
    this.listElement.current && this.listElement.current.forceUpdateGrid()
  }

  getFilteredItems = () => {
    const { items, searchText } = this.state
    if (searchText.length > 0) {
      return items.filter((item) => item.value && item.value.toString().toLowerCase().includes(searchText.toLowerCase()))
    }
    return items
  }

  setFilter = () => {
    const items = this.state.items[0]?.checked ? [] : this.state.items.filter((item) => item.checked).map(item => item.value)
    this.setState({ open: false, filtered: items.length > 0 })
    this.props.onFilter && this.props.onFilter(items)
  }

  handleSort = (order?: "asc" | "desc") => {
    this.setState({ sort: order, open: false })
    this.props.onSort && this.props.onSort(order)
  }

  rowRenderer = (props: ListRowProps) => {
    const item = this.getFilteredItems()[props.index]
    return (
      <Checkbox id={`${this.props.title}-${item.value}`} label={item.label} value={item.value.toString()} key={item.label} checked={item.checked} onChange={this.checkboxChange} style={props.style} />
    )
  }

  render() {
    return (
      <>
        <div onClick={this.toggleDialog} className="filter-toggle" ref={this.toggleElement}>
          <span className={`column-header-title ${this.props.uppercase ? "text-uppercase" : ""}`}>{this.props.title}</span>
          <span className="column-header-caret"><FaCaretDown /></span>
          <span hidden={this.state.sort !== "asc"}><FaArrowUp /></span>
          <span hidden={this.state.sort !== "desc"}><FaArrowDown /></span>
          <span hidden={!this.state.filtered}><FaFilter /></span>
          {this.props.children}
        </div>
        <Overlay container={this.toggleElement} target={this.toggleElement.current} show={this.state.open} placement="bottom-start" rootClose onHide={this.closeDialog} popperConfig={{modifiers:[{name:"offset", options:{offset:[this.props.offsetLeft, this.props.offsetTop]}}]}}>
          {({ arrowProps, show: _show, popper, ...props }) => (
            <div id="columnFilter" className="filter-section text-left" {...props}>
              <ul>
                <li onClick={() => this.handleSort("asc")} className={this.state.sort === "asc" ? "highlight" : ""}><FaSortAlphaDown />Sort Smallest to Largest</li>
                <li onClick={() => this.handleSort("desc")} className={this.state.sort === "desc" ? "highlight" : ""}><FaSortAlphaDownAlt />Sort Largest to Smallest</li>
                <li hidden={!this.state.filtered} onClick={() => this.clearFilter()}><img src={clearFilters} alt="" width={16} height={16} />Clear Filter From "{this.props.title}"</li>
              </ul>
              <hr />
              <div className="search-box">
                <Form.Control autoFocus tabIndex={0} type="text" size="sm" placeholder="Search" value={this.state.searchText} onChange={(e) => this.changeTextFilter(e.target.value)} ref={(input: HTMLInputElement) => input && input.focus()} />
                <FaSearch className="search-icon" />
                <div className="search-box-items">
                  <List ref={this.listElement} width={300} height={100} rowCount={this.getFilteredItems().length} rowHeight={20} rowRenderer={this.rowRenderer} />
                </div>
                {this.props.message && (<small>{this.props.message}</small>)}
                <div className="pt-1">
                  <Button size="sm" variant="secondary" className="float-right ml-1" onClick={this.closeDialog}>CANCEL</Button>
                  <Button size="sm" variant="secondary" className="float-right" disabled={this.state.items.every((item) => !item.checked)} onClick={this.setFilter}>OK</Button>
                </div>
              </div>
            </div>
          )}
        </Overlay>
      </>
    )
  }

  static defaultProps = { uppercase: true, offsetLeft: -13, offsetTop: 12 }
}
